<template>
  <v-app id="inspire">
    <v-card-title>
      Target Scoring Definition Data
      <v-dialog width="unset" height="unset" v-model="dialogs.upload">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="ml-5" color="secondary" v-bind="attrs" v-on="on"
            >Upload</v-btn
          >
        </template>
        <v-card class="pa-2" background="white" width="400">
          <v-card-title> Upload new templates from files </v-card-title>
          <v-card-text>
            <v-file-input
              label="Click to browse..."
              accept=".zip,.ets"
              filled
              show-size
              truncate-length="400"
              prepend-icon="mdi-paperclip"
              clear-icon="mdi-close"
              color="primary"
              v-model="file"
            />
          </v-card-text>
          <v-card-actions>
            <v-btn
              @click="uploadPackageFile()"
              right
              :loading="uploading"
              :disabled="uploading || !validFileSpecified"
              color="primary"
            >
              Upload
              <v-icon right dark>mdi-cloud-upload</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :search="search"
      :items="getTargetScoringDefinition"
      :expanded.sync="expanded"
      :hide-default-footer="true"
      :loading="$apollo.queries.getTargetScoringDefinition.loading"
      disable-pagination
      show-expand
      single-expand
      item-key="targetScoringDefinition.id"
      @click:row="refetchReleases()"
    >
      <template v-slot:item.released="{ item }">
        <v-icon v-if="item.released == 'RELEASED'">mdi-check</v-icon>
        <v-icon v-else>mdi-close</v-icon>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card-actions>
            <v-btn
              @click="
                generateRelease(item.targetScoringDefinition.id, 'RELEASE')
              "
              color="secondary"
              >Generate Release</v-btn
            >
          </v-card-actions>
          <v-text-field
            v-model="inputVersionFilter"
            label="Version Filter"
            name="inputVersionFilter"
            type="text"
            v-on:keyup.enter="submitVersionFilter"
          ></v-text-field>
          <v-data-table
            :headers="releaseHeaders"
            :items="getTargetScoringDefinitionReleaseByFilter"
          >
            <!-- ACTIONS -->
            <template v-slot:item.actions="{ item }">
              <!-- CHANGE PUBLICATION STATUS BUTTON -->
              <v-btn
                icon
                @click="
                  openPublicationStatusChangeDialog(
                    item.id,
                    item.publicationStatus
                  )
                "
              >
                <v-icon>mdi-earth</v-icon>
              </v-btn>

              <!-- DELETE BUTTON -->
              <v-btn
                icon
                @click="deleteShootingProgramTemplateRelease(item.id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <!-- DOWNLOAD BUTTON -->
              <v-btn icon @click="downloadRelease(item.binaryFileUuid)">
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>
    <c-confirmation-dialog ref="confirmationDialog"></c-confirmation-dialog>
    <v-dialog v-model="showPublicationStatusChangeDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <h3 class="publication-status-title">Change Publication Status</h3>
        </v-card-title>
        <v-card-text class="publication-status-container">
          <v-radio-group v-model="publicationStatus">
            <v-radio
              v-for="item in publicationStatusOptions"
              :key="item.value"
              :label="item.name"
              :value="item.value"
            ></v-radio>
          </v-radio-group>
          <v-btn @click="changePublicationStatus">Change</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<style scoped>
.publication-status-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.publication-status-title {
  width: 100%;
  text-align: center;
}
</style>

<script>
import {
  GET_TARGET_SCORING_DEFINITION_QUERY,
  GET_TARGET_SCORING_DEFINITION_RELEASE_FILTER_QUERY,
  UPLOAD_TARGET_SCORING_DEFINITION_MUTATION,
  UPLOAD_TARGET_SCORING_DEFINITION_RELEASE_GENERATE_MUTATION,
  DELETE_TARGET_SCORING_DEFINITION_RELEASE_MUTATION,
  UPDATE_TARGET_SCORING_DEFINITION_RELEASE_PUBLICATION_STATUS,
} from '../../graphql/targetScoringDefinitionQueries.js';

import { DownloadFileFromBinaryStorage } from '../../utils/UBinaryStorage';

export default {
  components: {
    CConfirmationDialog: () => import('../common/CConfirmationDialog'),
  },
  created() {
    this.refetchPackages();
  },
  data() {
    return {
      id: '',
      search: '',
      tag: '',
      expanded: [],
      publicationStatus: null, // The publication status to update the release to
      publicationStatusChangeReleaseId: null, // The template release to change the publication status of
      showPublicationStatusChangeDialog: false,
      publicationStatusOptions: [
        {
          name: 'Internal',
          value: 'INTERNAL',
        },
        {
          name: 'Public',
          value: 'PUBLIC',
        },
      ],
      headers: [
        { text: 'Name', value: 'targetScoringDefinition.name', sortable: true },
        {
          text: 'ID',
          value: 'targetScoringDefinition.id',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Distribution ID',
          value: 'distributionBinaryFileId',
          sortable: false,
        },
        {
          text: 'Visual number',
          value: 'targetScoringDefinition.visualNumber',
          sortable: true,
        },
        {
          text: 'Version',
          value: 'targetScoringDefinition.version',
          sortable: true,
        },
        {
          text: 'Organization Target Name',
          value: 'targetScoringDefinition.organizationTargetName',
          sortable: true,
        },
        {
          text: 'Organization Target ID',
          value: 'targetScoringDefinition.organizationTargetId',
          sortable: false,
        },
        { text: 'Released', value: 'released', sortable: false },
      ],
      releaseHeaders: [
        // { text: 'ID', value: 'id', align: 'left', sortable: false },
        // { text: 'Ver. Major', value: 'versionMajor', sortable: true },
        // { text: 'Ver. Minor', value: 'versionMinor', sortable: true },
        // { text: 'Ver. Patch', value: 'versionPatch', sortable: true },
        // { text: 'Ver. Pre.', value: 'versionPrerelease', sortable: false },
        // { text: 'Ver. Nr.', value: 'versionPrereleaseNumber', sortable: true },
        { text: 'Version', value: 'version', sortable: true },
        { text: 'Date', value: 'dateTime', sortable: false },
        // { text: 'Binary File ID', value: 'binaryFileUuid', sortable: false },
        { text: 'Publication', value: 'publicationStatus', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          width: '18%',
        },
      ],
      getTargetScoringDefinition: [],
      getTargetScoringDefinitionReleaseByFilter: [],
      dialogs: {
        upload: false,
      },
      file: {},
      uploading: false,
      inputVersionFilter: '',
      versionFilter: '',
    };
  },
  apollo: {
    $client: 'targetScoringDefinitionClient',
    getTargetScoringDefinition: {
      query: GET_TARGET_SCORING_DEFINITION_QUERY,
      loadingKey: 'loading',
      options: { fetchPolicy: 'cache-and-network' },
    },
    getTargetScoringDefinitionReleaseByFilter: {
      query: GET_TARGET_SCORING_DEFINITION_RELEASE_FILTER_QUERY,
      variables() {
        // Reactive parameters
        return {
          id: this.expanded[0].targetScoringDefinition.id, // Template ID
          versionFilter: this.versionFilter,
          limit: 5,
        };
      },
      skip() {
        return this.expanded.length === 0; // Skip query when not expanded
      },
      loadingKey: 'loading',
    },
    getTargetScoringDefinitionFirstReleaseByFilter: {
      query: GET_TARGET_SCORING_DEFINITION_RELEASE_FILTER_QUERY,
      variables() {
        // Reactive parameters
        return {
          id: this.expanded[0].targetScoringDefinition.id, // Template ID
          versionFilter: this.versionFilter,
          limit: 1,
        };
      },
      skip() {
        return this.expanded.length === 0; // Skip query when not expanded
      },
      loadingKey: 'loading',
    },
  },
  computed: {
    validFileSpecified() {
      if (this.file === undefined) {
        return false;
      }

      return this.file.name !== undefined && this.file.size > 0;
    },
  },
  methods: {
    refetchPackages() {
      this.$apollo.queries.getTargetScoringDefinition.refetch();
    },
    refetchReleases() {
      this.$apollo.queries.getTargetScoringDefinitionReleaseByFilter.refetch();
      this.versionFilter = '';
    },

    async uploadPackageFile() {
      if (this.validFileSpecified) {
        try {
          // Set uploading state to indicate that the upload process is ongoing
          this.uploading = true;

          await this.$apollo.mutate({
            mutation: UPLOAD_TARGET_SCORING_DEFINITION_MUTATION,
            variables: {
              file: this.file,
            },
            context: {
              hasUpload: true,
            },
          });

          this.uploading = false;
          this.file = {};
          this.dialogs.upload = false;

          this.refetchPackages();
        } catch (error) {
          console.error(error);
          this.uploading = false;
        }
      }
    },
    async generateRelease(id, increaseVersion) {
      await this.$apollo.mutate({
        mutation: UPLOAD_TARGET_SCORING_DEFINITION_RELEASE_GENERATE_MUTATION,
        variables: {
          id: id,
          increaseVersion: increaseVersion,
        },
      });
      this.refetchPackages();
      this.refetchReleases();
    },
    async downloadRelease(binaryFileId) {
      DownloadFileFromBinaryStorage(binaryFileId);
    },
    async deleteShootingProgramTemplateRelease(id) {
      if (
        await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this shooting program template release?'
        )
      ) {
        try {
          await this.$apollo.mutate({
            mutation: DELETE_TARGET_SCORING_DEFINITION_RELEASE_MUTATION,
            variables: {
              id: id,
            },
          });
        } catch (err) {
          alert('Error deleting template! ' + err.message);
        }
        this.refetchReleases();
      }
    },
    openPublicationStatusChangeDialog(
      targetDefinitionReleaseId,
      currentPublicationStatus
    ) {
      this.publicationStatus = currentPublicationStatus;
      this.publicationStatusChangeReleaseId = targetDefinitionReleaseId;
      this.showPublicationStatusChangeDialog = true;
    },
    async changePublicationStatus() {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_TARGET_SCORING_DEFINITION_RELEASE_PUBLICATION_STATUS,
          variables: {
            id: this.publicationStatusChangeReleaseId,
            publicationStatus: this.publicationStatus,
          },
        });
        this.showPublicationStatusChangeDialog = false;
        this.publicationStatusChangeReleaseId = null;
      } catch (error) {
        alert('Error changing publication status! ' + error.message);
      }
      this.refetchReleases();
    },
  },
};
</script>
